/* Holding a just-returned row until its question is answered — STORY-030.
 *
 * ── this is the defect that keeps coming back ───────────────────────────────
 * The story records it being found on 2026-08-11: marking one item returned
 * from three places gave three different outcomes, because the condition
 * follow-up was captured or lost depending on which screen you were on. It was
 * then reproduced twice more during this build, both times MECHANICALLY rather
 * than by the screens disagreeing:
 *
 *   · the still-out list moved the row to a different list on return, which
 *     unmounted the control that had just opened the question;
 *   · the Event's loadout hid the control entirely once `returnedAt` was set,
 *     which did the same thing by a different route — and the loadout's own
 *     header comment claimed it was immune because it used the shared control.
 *
 * Sharing the CONTROL was never enough. What has to be shared is the rule that
 * a returned row stays on screen, and stays mounted, until its question is
 * finished with. That rule lives here, and a screen that marks kit returned
 * without it is the next recurrence.
 */

import type { Assignment } from '@eventuley/contracts';
import { useState } from 'react';

export interface ReturnHolding {
  /** Rows the reader has returned and not yet finished answering about. */
  readonly held: readonly Assignment[];
  /** True while this row must stay on screen with its question open. */
  isHeld(assignmentId: string): boolean;
  /** The write landed. Hold the row. */
  hold(assignment: Assignment): void;
  /** The question is answered or dismissed. Let it go. */
  release(assignmentId: string): void;
}

export function useReturnHolding(): ReturnHolding {
  const [held, setHeld] = useState<readonly Assignment[]>([]);

  return {
    held,
    isHeld: (assignmentId) => held.some((row) => row.id === assignmentId),

    /* The updater form, and not `[...held, row]`.
     *
     * Two rows returned in quick succession — both mutations resolving before a
     * re-render — made the second callback overwrite the first from a stale
     * closure, dropping row A from the held list and losing its unanswered
     * follow-up. Which is precisely the outcome this whole mechanism exists to
     * prevent, arrived at by the mechanism itself. */
    hold: (assignment) =>
      setHeld((current) =>
        current.some((row) => row.id === assignment.id) ? current : [...current, assignment],
      ),

    release: (assignmentId) =>
      setHeld((current) => current.filter((row) => row.id !== assignmentId)),
  };
}
